import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { debounce } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { validatePermission } from "../../../utils/permissionUtils";
import { usePermissions } from "../../../context/permissionContext";
import { DataGrid } from "@mui/x-data-grid";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import useResponsiveColumns from "../../../hooks/useResponsiveColumns";
import { newsPostUrl } from "../../../utils/portalUrl";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Responsive column visibility (omitted fields stay visible).
const NEWS_COL_PRIORITY = {
  id: "low",
  updatedAt: "low",
  description: "medium",
  categoryNames: "medium",
};

// Plain-text preview of a description. DOMParser builds an INERT document:
// nothing in it loads or runs. The previous document.createElement("DIV")
// .innerHTML = str belonged to the live page, so an <img onerror> in a post
// would have run in the admin's session when the list rendered.
export function descriptionPreview(html, wordLimit = 10) {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  // Pad block boundaries so "<p>two</p><p>three</p>" reads "two three".
  doc.body.querySelectorAll("p,div,li,h1,h2,h3,h4,h5,h6,br,figure,blockquote,tr,td,th").forEach((el) => {
    el.insertAdjacentText("afterend", " ");
  });
  const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  const words = text.split(" ");
  return words.length > wordLimit ? `${words.slice(0, wordLimit).join(" ")}...` : text;
}

const NewsList = () => {
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("2");
  const { permissions, permissionsMapping } = usePermissions();
  const handleQuery = useCallback(
    debounce((e) => {
      setQuery(e.target.value);
      setPageState((prev) => ({ ...prev, page: 1 }));
    }, 1000),
    []
  );
  const hasEditPermission = validatePermission(
    "News:Edit",
    permissions,
    permissionsMapping
  );
  const hasDeletePermission = validatePermission(
    "News:Delete",
    permissions,
    permissionsMapping
  );

  const fetchuserData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      setPageState((old) => ({ ...old }));

      const config = {
        method: "post",
        url: process.env.REACT_APP_API + "admin/get-news",
        headers: { "x-access-token": token },
        data: {
          pagination: true,
          page: pageState.page,
          limit: pageState.pageSize,
          searchTerm: query,
          status: status,
        },
      };

      const response = await axios(config);
      setLoading(false);
      setPageState((old) => ({
        ...old,
        data: response.data.data.rows,
        total: response.data.data.count,
      }));
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: error?.response?.data?.message,
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  const deleteFunction = (id) => {
    const token = localStorage.getItem("accessToken");
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this news!",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios
          .delete(process.env.REACT_APP_API + `admin/news/${id}`, {
            headers: { "x-access-token": token },
          })
          .then(function (response) {
            if (response?.status == 200) {
              setLoading(false);
              fetchuserData();
            }
          })
          .catch(function (error) {
            setLoading(false);
            Swal.fire({
              title: error?.response?.data?.message,
              timerProgressBar: true,
              icon: "error",
              confirmButtonText: "OK",
              showCloseButton: true,
            });
          });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        setLoading(false);
        Swal.fire("Cancelled", "News is not deleted", "error");
      }
    });
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        
        return (
          <div className="cellAction">
            <Link
              to={`/news/${params.row.id}`}
              style={{ textDecoration: "none" }}
            >
              <div className="viewButton">
                <RemoveRedEyeOutlinedIcon />
              </div>
            </Link>
            {hasEditPermission && <Link
              to={`/news/edit-news/${params.row.id}`}
              style={{ textDecoration: "none" }}
            >
              <div className="viewButton">
                <EditOutlinedIcon />
              </div>
            </Link>}

            {Number(params.row.status) === 1 && newsPostUrl(params.row.slug) && (
              <a
                href={newsPostUrl(params.row.slug)}
                target="_blank"
                rel="noopener noreferrer"
                title="View on site"
                aria-label="View on site"
                style={{ textDecoration: "none" }}
              >
                <div className="viewButton">
                  <OpenInNewIcon />
                </div>
              </a>
            )}

            {hasDeletePermission && <div
              className="deleteButton"
              onClick={() => deleteFunction(params?.row?.id)}
            >
              <DeleteOutlineIcon />
            </div>}
          </div>
        );
      },
    },
  ];

  const columnVisibilityModel = useResponsiveColumns(NEWS_COL_PRIORITY);

  const userColumns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 500,
      renderCell: (params) => <div>{descriptionPreview(params.value, 10)}</div>,
    },
    {
      field: "categoryNames",
      headerName: "Category",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        (Array.isArray(params.row.category) ? params.row.category : [])
          .map((c) => c && c.category)
          .filter(Boolean)
          .join(", "),
    },
    {
      field: "event_date",
      headerName: "Event date",
      flex: 1,
      minWidth: 130,
      // Calendar day as stored (midday UTC); slicing avoids a timezone shift.
      renderCell: (params) => (params.value ? String(params.value).slice(0, 10) : "-"),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <span
              className={`status ${
                params.row.status == 0 ? "Pending" : "Approved"
              }`}
            >
              {params?.row?.status == 0 ? "Draft" : "Published"}
            </span>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    fetchuserData();
  }, [pageState.page, pageState.pageSize, query, status]);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="News" />
      <div className="datatable">
      <div className="datatableTitle">
        <div className="tablesearch">
          <input type="text" placeholder="Search..." onChange={handleQuery} />
          <SearchOutlinedIcon className="sarchicon" />
        </div>
        <div className="formInput">
          <select name="filter" onChange={(e) => setStatus(e.target.value)}>
            <option value="" disabled selected hidden>
              Select Status
            </option>
            <option value={2}>All</option>
            <option value={0}>Draft</option>
            <option value={1}>Published</option>
          </select>
        </div>
        {hasEditPermission && <Link to="/news/add-news" className="link">
          Add New
        </Link>}
      </div>
      <DataGrid
        className="datagrid"
        autoHeight
        rows={pageState.data}
        rowCount={pageState.total}
        rowsPerPageOptions={[10, 30, 50, 70, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPageState((old) => ({ ...old, page: newPage + 1 }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        columns={userColumns.concat(actionColumn)}
        columnVisibilityModel={columnVisibilityModel}
      />
      </div>
    </PageShell>
  );
};

export default NewsList;
